import React from 'react';
import { createSwitchNavigator } from 'react-navigation';
import {
  UserDataConsumer,
  UserDataProvider,
} from 'fitbeat.react-native-components';
import { createBrowserApp } from '@react-navigation/web';
import { AuthProvider } from '../AuthProvider';
import { DashboardContainer } from '../Components/DashboardContainer';
import { NotificationsScreenContainer } from '../Components/NotificationsScreenContainer';
import { FaqScreenContainer } from '../Components/FAQScreenContainer';
import { HomeLoadingContainer } from '../Components/HomeLoadingContainer';
import { HelpNavigator } from './HelpNavigator';
import { NutritionScreenContainer } from '../Components/NutritionContainer';
import { StatsScreenContainer } from '../Components/StatsScreenContainer';
import { PreLoadedPlanDetailsContainer } from '../Components/PreLoadedPlanDetailsContainer';
import { ExternalPlanSelectionContainer } from '../Components/ExternalPlanSelectionContainer';
import { CaptureMissingDetailsContainer } from '../Components/CaptureMissingDetailsContainer';
import { CaptureMissingGuardianDetailsContainer } from '../Components/CaptureMissingGuardianDetailsContainer';
import { SkillLevelContainer } from '../Components/SkillLevelContainer';
import { LongTermGoalContainer } from '../Components/LongTermGoalContainer';
import { LongTermGoalDescriptionContainer } from '../Components/LongTermGoalDescriptionContainer';
import { OnBoardingCompleteContainer } from '../Components/OnBoardingCompleteContainer';
import { ProfileNavigationContainer } from '../Components/ProfileNavigationContainer';
import { WorkoutHistoryScreenContainer } from '../Components/WorkoutHistoryContainer';
import { TrackingWorkoutStatsContainer } from '../Components/TrackingWorkoutStatsContainer';
import { HealthStatsHistoryScreenContainer } from '../Components/HealthStatsHistoryContainer';
import { TrackingScanStatsContainer } from '../Components/TrackingScanStatsContainer';
import { SummaryDashboardContainer } from '../Components/SummaryDashboardContainer';
import { HomeInstallAppContainer } from '../Components/HomeInstallAppContainer';
import { InvitationCodeWelcomeContainer } from '../Components/InvitationCodeWelcomeContainer';
import { HomeCarouselAuthLayout } from '../Components/Layouts/HomeCarouselAuthLayout';
import { CreateDemoStudioBookingContainer } from '../Components/CreateDemoStudioBookingContainer';
import { StripeAccountChangeConfirmationContainer } from '../Components/StripeAccountChangeConfirmationContainer';
import { LeaderboardStatsContainer } from '../Components/LeaderboardStatsContainer';
import { LeaderboardTeamContainer } from '../Components/LeaderboardTeamContainer';
import { CompletedLeaderboardsContainer } from '../Components/CompletedLeaderboardsContainer';
import LeaderboardUserBreakdownContainer from '../Components/LeaderboardUserScoreBreakdownContainer';

const HomeNavigator = createSwitchNavigator({
  HomeLoading: {
    screen: HomeLoadingContainer,
    path: 'homeLoading',
  },
  CaptureMissingBasicDetails: {
    screen: CaptureMissingDetailsContainer,
    path: 'details',
  },
  CaptureMissingGuardianDetails: {
    screen: CaptureMissingGuardianDetailsContainer,
    path: 'guardian-details',
  },
  LongTermGoal: {
    screen: LongTermGoalContainer,
    path: 'long-term-goal',
  },
  LongTermGoalDescription: {
    screen: LongTermGoalDescriptionContainer,
    path: 'long-term-goal-info',
  },
  SkillLevel: {
    screen: SkillLevelContainer,
    path: 'skill-level',
  },
  OnBoardingComplete: {
    screen: OnBoardingCompleteContainer,
    path: 'signup-complete',
  },
  CreateDemoStudioBooking: {
    screen: CreateDemoStudioBookingContainer,
    path: 'create-demo-studio-booking',
  },
  UserDashboard: {
    screen: UserDataConsumer(DashboardContainer),
    path: 'dashboard',
    navigationOptions: () => ({
      title: 'Planner',
    }),
  },
  Nutrition: {
    screen: NutritionScreenContainer,
    path: 'nutrition',
    navigationOptions: () => ({
      title: 'Meals',
    }),
  },
  StatsScreen: {
    screen: StatsScreenContainer,
    path: 'stats',
    navigationOptions: () => ({
      title: 'Progress',
    }),
  },
  Notifications: {
    screen: NotificationsScreenContainer,
    path: 'notifications',
    navigationOptions: () => ({
      title: 'Notifications',
    }),
  },
  Help: {
    screen: HelpNavigator,
    path: 'help',
    navigationOptions: () => ({
      title: 'Help',
    }),
  },
  FaqScreen: {
    screen: FaqScreenContainer,
    path: 'faqscreen',
  },
  PreLoadedPlanDetails: {
    screen: PreLoadedPlanDetailsContainer,
    path: 'preloaded-plan-details',
  },
  ExternalPlanSelection: {
    screen: ExternalPlanSelectionContainer,
    path: 'plan-selection',
  },
  StripeAccountChangeConfirmation: {
    screen: StripeAccountChangeConfirmationContainer,
    path: 'account-change-confirmation',
  },
  History: {
    screen: WorkoutHistoryScreenContainer,
    path: 'History',
    navigationOptions: () => ({
      title: 'History',
    }),
  },
  ScanHistory: {
    screen: HealthStatsHistoryScreenContainer,
    path: 'ScanHistory',
    navigationOptions: () => ({
      title: 'Scan History',
    }),
  },
  TrackingDashboard: {
    screen: StatsScreenContainer,
    path: 'TrackingDashboard',
    navigationOptions: () => ({
      title: 'Progress',
    }),
  },
  TrackingWorkoutStats: {
    screen: TrackingWorkoutStatsContainer,
    path: 'TrackingWorkoutStats',
    navigationOptions: () => ({
      title: 'Workout Details',
    }),
  },
  TrackingScanStats: {
    screen: TrackingScanStatsContainer,
    path: 'TrackingScanStats',
    navigationOptions: () => ({
      title: 'Health Details',
    }),
  },
  LeaderboardStats: {
    screen: LeaderboardStatsContainer,
    path: 'LeaderboardStats',
    navigationOptions: ({ navigation }) => ({
      title: 'Leaderboard',
    }),
  },
  LeaderboardTeam: {
    screen: LeaderboardTeamContainer,
    path: 'LeaderboardTeams',
    navigationOptions: ({ navigation }) => ({
      title: navigation.state?.params?.team?.toUpperCase?.(),
    }),
  },
  CompletedLeaderboards: {
    screen: CompletedLeaderboardsContainer,
    path: 'CompletedLeaderboards',
    navigationOptions: ({ navigation }) => ({
      title: 'COMPLETED LEADERBOARDS',
    }),
  },
  LeaderboardUserBreakdown: {
    screen: LeaderboardUserBreakdownContainer,
    path: 'LeaderboardUserBreakdown',
    navigationOptions: ({ navigation }) => ({
      title: navigation.state?.params?.userName?.toUpperCase?.() ?? 'User',
    }),
  },
  Profile: {
    screen: ProfileNavigationContainer,
    path: 'profile',
    navigationOptions: () => ({
      title: 'More',
    }),
  },
  SummaryDashboard: {
    screen: SummaryDashboardContainer,
    path: 'SummaryDashboard',
    navigationOptions: () => ({
      title: 'Home',
    }),
  },
  HomeInstallAppScreen: {
    screen: HomeInstallAppContainer,
    path: 'install-app',
    navigationOptions: () => ({
      title: 'Install Fitillion App',
    }),
  },
  InvitationCodeWelcome: {
    screen: InvitationCodeWelcomeContainer,
    path: 'invitation-code-welcome',
    navigationOptions: () => ({
      header: null,
    }),
  },
});

export default UserDataProvider(createBrowserApp(HomeNavigator), new AuthProvider(), HomeCarouselAuthLayout);
